import {
  ActionIcon,
  Divider,
  Grid,
  Group,
  Modal,
  NumberInput,
  Paper,
  Select,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import {
  useEffect,
  useMemo,
  useState,
} from 'react'

import {
  IconActivityHeartbeat,
  IconCalculator,
} from '@tabler/icons-react'
import Device from '@data/device/model/device'
import {
  DEVICE_STATE_LABELS,
  DEVICE_STATES,
} from '@data/device/model/deviceState'
import {
  SECTOR_TYPES_OPTIONS,
  UPLOAD_FREQ_MINS,
} from '@components/main/DeviceManagement/DeviceInformationManager/constants'
import { pulseOptions } from '@common/utils/helperFunctions'
import { UpdateButton } from '@components/main/DeviceManagement/DeviceInformationManager/DeviceModalMantine/components/UpdateButton'
import { DirtySelect } from '@components/ui/form/DirtySelect'
import { DirtySwitch } from '@components/ui/form/DirtySwitch'
import { DirtyFieldLabel } from '@components/ui/form/dirty-field'
import { DirtyDateTimePicker } from '@components/ui/form/DirtyDateTimePicker'
import {
  mantineDateToUtc,
  utcToMantineDate,
} from '@common/utils/datetime/timezone'
import {
  Settings,
  TriangleAlert,
} from 'lucide-react'

export interface DeviceMonitoringTabProps {
  device: Device
  deviceAdmin: boolean
  deviceUpdating: boolean

  onUpdate: (
    device: Device,
  ) => Promise<void>

  onUpdatePulses: (
    pulses: number,
  ) => Promise<void>
}

const getInitialValues = (
  device: Device,
) => ({
  installDate: utcToMantineDate(
    device.deviceSettings.installDate,
    device.deviceSettings.timezone,
  ),

  usageStartDate:
    utcToMantineDate(
      device.deviceSettings.usageStartDate,
      device.deviceSettings.timezone,
    ),

  monitoringStartDate:
    utcToMantineDate(
      device.deviceSettings.monitoringStartDate,
      device.deviceSettings.timezone,
    ),

  uploadFreqMins: device.deviceSettings.uploadFreqMins,
  sectorType: device.deviceSettings.sectorType,
  waterType: device.deviceSettings.type,
  monitoringEnabled: device.deviceSettings.monitoringEnabled,
  aasEnabled: device.deviceSettings.aasEnabled,
  usageLoggingEnabled: device.deviceSettings.usageLoggingEnabled,
  deviceState: device.deviceSettings.deviceState,
  vacant: device.deviceSettings.vacant,
  noFlowThresholdHours: device.deviceSettings.noFlowThresholdHours,
  missedCheckinThreshold: device.deviceSettings.missedCheckinThreshold,
})

export const DeviceMonitoringTab = ({
                                      device,
                                      deviceAdmin,
                                      deviceUpdating,
                                      onUpdate,
                                      onUpdatePulses,
                                    }: DeviceMonitoringTabProps) => {
  const [newPulses, setNewPulses] = useState<number>(device.deviceSettings.pulsesPerLiter)
  const [calcOpen, setCalcOpen] = useState(false)
  const [calcPulses, setCalcPulses] = useState<number>(1)
  const [calcKFactor, setCalcKFactor] = useState<string | null>(String(device.deviceSettings.pulsesPerLiter))

  const form = useForm({
    initialValues: getInitialValues(device),
  })

  useEffect(() => {
    form.setValues(
      getInitialValues(device),
    )

    form.resetDirty()

    setNewPulses(device.deviceSettings.pulsesPerLiter)
  }, [device])

  const pulsesDirty = newPulses !== device.deviceSettings.pulsesPerLiter

  const handleSubmit = async (values: typeof form.values) => {
    const updatedDevice: Device = {
      ...device,
      deviceSettings: {
        ...device.deviceSettings,

        installDate:
          mantineDateToUtc(values.installDate, device.deviceSettings.timezone) ||
          device.deviceSettings.installDate,

        usageStartDate:
          mantineDateToUtc(values.usageStartDate, device.deviceSettings.timezone) ||
          device.deviceSettings.usageStartDate,

        monitoringStartDate:
          mantineDateToUtc(values.monitoringStartDate, device.deviceSettings.timezone) ||
          device.deviceSettings.monitoringStartDate,

        uploadFreqMins: values.uploadFreqMins,
        sectorType: values.sectorType,
        type: values.waterType,
        hot: values.waterType === 'Hot',
        monitoringEnabled: values.monitoringEnabled,
        aasEnabled: values.aasEnabled,
        usageLoggingEnabled: values.usageLoggingEnabled,
        deviceState: values.deviceState,
        vacant: values.vacant,
        noFlowThresholdHours: values.noFlowThresholdHours,
        missedCheckinThreshold: values.missedCheckinThreshold,
      },
    }

    await onUpdate(
      updatedDevice,
    )
  }

  const pulseSelectOptions =
    useMemo(() => {
      return pulseOptions(
        device.deviceSettings.uom,
      )
    }, [device.deviceSettings.uom])

  return (
    <form
      onSubmit={form.onSubmit(
        handleSubmit,
      )}
    >
      <Stack gap='lg'>
        <Text
          size='xs'
          c={'red'}
        >
          *All dates/times are displayed in the device timezone
        </Text>
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <DirtyDateTimePicker
              form={form}
              field='installDate'
              label='Install Date'
              disabled={!deviceAdmin}
              placeholder='Select install date'
              tooltip='The date the device was installed and added to the system'
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <DirtyDateTimePicker
              form={form}
              field='usageStartDate'
              label='Usage Start Date'
              disabled={!deviceAdmin}
              placeholder='Select usage start date'
              tooltip='Usage data from this date forward will be returned and displayed.'
            />
          </Grid.Col>
        </Grid>

        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <DirtyDateTimePicker
              form={form}
              field='monitoringStartDate'
              label='Monitoring Start Date'
              disabled={!deviceAdmin}
              placeholder='Select monitoring start date'
              tooltip='The monitoring system will only generate alerts on usage data from this date onwards.'
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <DirtySelect
              form={form}
              field='uploadFreqMins'
              label='Checkin Interval (hours)'
              data={UPLOAD_FREQ_MINS.map(
                (freq) => ({ value: String(freq), label: String(freq / 60) }),
              )}
              disabled={!deviceAdmin}
              tooltip='The frequency at which the device uploads usage data. This value determines how quickly
              the system identifies a device as offline.'
            />
          </Grid.Col>
        </Grid>

        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <DirtySelect
              form={form}
              field='sectorType'
              label='Sector Type'
              data={SECTOR_TYPES_OPTIONS}
              disabled={!deviceAdmin}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <DirtySelect
              form={form}
              field='waterType'
              label='Water Type'
              data={[
                {
                  value: 'Cold',
                  label: 'Cold',
                },
                {
                  value: 'Hot',
                  label: 'Hot',
                },
              ]}
              disabled={!deviceAdmin}
            />
          </Grid.Col>
        </Grid>

        <Divider />

        <Group gap='xs'>
          <Settings
            size={18}
          />
          <Text fw={600}>
            Ticket Configuration
          </Text>
        </Group>

        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <DirtySelect
              form={form}
              field='noFlowThresholdHours'
              label='No Flow Threshold (hours)'
              data={[12, 24, 36, 48, 60, 72, 84, 96, 108, 120, 132, 144, 156, 160].map(
                (h) => ({ value: String(h), label: String(h) }),
              )}
              disabled={!deviceAdmin}
              tooltip='Number of hours without flow before a No Flow ticket is generated.'
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <DirtySelect
              form={form}
              field='missedCheckinThreshold'
              label='Max Missed Checkins'
              data={Array.from({ length: 10 }, (_, i) => i + 3).map(
                (n) => ({ value: String(n), label: String(n) }),
              )}
              disabled={!deviceAdmin}
              tooltip='Number of consecutive missed checkins before an Offline ticket is generated.'
            />
          </Grid.Col>
        </Grid>

        <Divider />

        <Group gap='xs'>
          <TriangleAlert
            size={18}
          />
          <Text fw={600}>
            Alert/Usage Configuration
          </Text>
          <Text
            fw={600}
            c='red'
          >
            **Danger Zone**
          </Text>
        </Group>
        <Paper
          withBorder
          p='md'
          radius='md'
          bd='1px solid var(--mantine-color-red-6)'
        >
          <Grid justify='space-between' align='flex-start'>
            <Grid.Col span={{ base: 12, md: 4 }}>
              <DirtySwitch
                form={form}
                field='monitoringEnabled'
                label='Monitoring'
                disabled={!deviceAdmin}
                tooltip='Generate usage alerts for the device'
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}>
              <DirtySwitch
                form={form}
                field='aasEnabled'
                label='Auto Alerts'
                disabled={!deviceAdmin}
                tooltip='Enable the Auto Alerts service'
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}>
              <DirtySwitch
                form={form}
                field='usageLoggingEnabled'
                label='Usage Logging'
                disabled={!deviceAdmin}
                tooltip={
                  <>
                    Enable to allow the device to upload usage data. <strong>Note:</strong> Disable with caution.
                  </>
                }
              />
            </Grid.Col>
          </Grid>
        </Paper>

        <Divider />

        <Group gap='xs'>
          <Settings
            size={18}
          />
          <Text fw={600}>
            Device State Configuration
          </Text>
        </Group>
        <Grid justify='space-between' align='flex-start'>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <DirtySelect
              form={form}
              field='deviceState'
              label='Device State'
              data={DEVICE_STATES.map(
                (state) => ({ value: state, label: DEVICE_STATE_LABELS[state] }),
              )}
              allowDeselect={false}
              disabled={!deviceAdmin}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <DirtySwitch
              form={form}
              field='vacant'
              label='Vacant'
              disabled={!deviceAdmin}
              tooltip='Highligts if an assets is vacant. Visiblie in the Alert Queue or ESG device menu as information flag only'
            />
          </Grid.Col>
        </Grid>

        <Divider />

        <UpdateButton
          type='submit'
          buttonText='Update Monitoring Settings'
          loading={deviceUpdating && form.isDirty()}
          disabled={!form.isDirty()}
        />

        <Divider />

        {/* PULSES */}
        <Stack gap='md'>
          <Group gap='xs'>
            <IconActivityHeartbeat
              size={18}
            />
            <Text fw={600}>
              Pulse Configuration
            </Text>
            <Tooltip label='Open pulse calculator'>
              <ActionIcon
                variant='subtle'
                color='gray'
                size='sm'
                onClick={() => {
                  setCalcKFactor(String(newPulses))
                  setCalcPulses(1)
                  setCalcOpen(true)
                }}
              >
                <IconCalculator size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>

          <Modal
            opened={calcOpen}
            onClose={() => setCalcOpen(false)}
            title={
              <Group gap='xs'>
                <IconCalculator size={18} />
                <Text fw={600}>Pulse Conversion Calculator</Text>
              </Group>
            }
            centered
          >
            <Stack gap='md'>
              <NumberInput
                label='Number of Pulses'
                placeholder='Enter pulse count'
                min={1}
                value={calcPulses}
                onChange={(
                  value
                ) =>
                  setCalcPulses(
                    Number(value)
                  )
              }
              />

              <Select
                label={`Pulses per ${device.deviceSettings.uom} (K-Factor)`}
                data={pulseSelectOptions}
                value={calcKFactor}
                onChange={setCalcKFactor}
                placeholder='Select K-Factor'
              />

              <Paper
                withBorder
                p='md'
                radius='md'
                bg='var(--mantine-color-default)'
              >
                <Stack gap={4}>
                  <Text size='xs' c='dimmed'>Result</Text>
                  <Text fw={700} size='xl'>
                    {calcKFactor && Number(calcKFactor) > 0
                      ? `${(Number(calcPulses) / Number(calcKFactor)).toFixed(4)} ${device.deviceSettings.uom}`
                      : '—'
                    }
                  </Text>
                  {calcKFactor && Number(calcKFactor) > 0 && (
                    <Text size='xs' c='dimmed'>
                      {calcPulses} pulses ÷ {calcKFactor} pulses/{device.deviceSettings.uom}
                    </Text>
                  )}
                </Stack>
              </Paper>
            </Stack>
          </Modal>

          <Grid align='flex-end'>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Select
                label={
                  <DirtyFieldLabel
                    label={`Pulses per ${device.deviceSettings.uom}`}
                    changed={newPulses !== device.deviceSettings.pulsesPerLiter}
                    tooltip='The "K" factor of the pulde output meter.'
                  />
                }
                disabled={!deviceAdmin}
                value={String(newPulses)}
                onChange={(
                  value,
                ) =>
                  setNewPulses(
                    Number(value),
                  )
                }
                data={pulseSelectOptions}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <UpdateButton
                buttonText='Set Pulses'
                disabled={!pulsesDirty}
                loading={deviceUpdating && pulsesDirty}
                loadingText='Updating...'
                onClick={() => onUpdatePulses(newPulses)}
              />
            </Grid.Col>
          </Grid>
        </Stack>
      </Stack>
    </form>
  )
}